import { create } from "zustand"
import { persist } from "zustand/middleware"
import { deviceController } from "./device-controller"
import { useScriptPlaybackStore } from "./script-playback-store"
import { HandyDevice } from "./devices/handy-device"

export type DeviceType = "handy" | "lovense" | "kiiroo" | "osr2" | "sr6" | "buttplug" | "other"
export type ConnectionType = "bluetooth" | "wifi" | "usb" | "server"
export type ControlMode = "2-axis" | "y-only"

export interface DevicePreset {
  minStroke: number
  maxStroke: number
  minSpeed: number
  maxSpeed: number
}

export const DEVICE_PRESETS: Record<DeviceType, DevicePreset> = {
  handy: {
    minStroke: 0,
    maxStroke: 100,
    minSpeed: 0,
    maxSpeed: 100,
  },
  lovense: {
    minStroke: 0,
    maxStroke: 100,
    minSpeed: 0,
    maxSpeed: 100,
  },
  kiiroo: {
    minStroke: 0,
    maxStroke: 100,
    minSpeed: 0,
    maxSpeed: 80,
  },
  osr2: {
    minStroke: 0,
    maxStroke: 100,
    minSpeed: 0,
    maxSpeed: 100,
  },
  sr6: {
    minStroke: 0,
    maxStroke: 100,
    minSpeed: 0,
    maxSpeed: 90,
  },
  buttplug: {
    minStroke: 0,
    maxStroke: 100,
    minSpeed: 0,
    maxSpeed: 100,
  },
  other: {
    minStroke: 0,
    maxStroke: 100,
    minSpeed: 0,
    maxSpeed: 100,
  },
}

export interface DeviceInfo {
  id: string
  name: string
  type: DeviceType
  connectionType: ConnectionType
  connectionKey?: string
  /** Handy-only: which Handy API version to talk to. "v2" (stable, default)
   * works with firmware 3+; "v3" (beta) is needed going forward for
   * firmware 4+ but is itself still beta. Ignored by non-Handy device types. */
  apiVersion?: "v2" | "v3"
  isConnected: boolean
  lastPingSuccess: boolean
  lastPingTime: number
  batteryLevel?: number
  firmwareVersion?: string
  lastConnected?: number
  latencyMs: number
  /** Sync delay applied while this device is active, in ms. Negative = fires earlier, positive = fires later. */
  syncDelayMs?: number
  settings: DeviceSettings
}

export interface DeviceSettings {
  minPosition: number // 0-100
  maxPosition: number // 0-100
  invertControls: boolean
  strokeSpeedLimit: number // max strokes per second
  restPosition: number // 0-100 — where device goes when not playing
  smoothingEnabled: boolean
  smoothingAmount: number // 0-100
  controlMode: ControlMode
  yOnlySpeedSensitivity: number // 0-100
}

interface DeviceState {
  devices: DeviceInfo[]
  activeDeviceId: string | null
  /** Ids removed via removeDevice, kept around (and persisted the same as
   * `devices`) purely so use-device-sync's "pull once on login" can refuse
   * to restore them. A delete's push to Supabase is async — a reload that
   * lands before that request actually completes still pulls the stale,
   * pre-delete remote list, and without this guard would silently restore
   * the "deleted" device and immediately re-save it, permanently undoing
   * the deletion. This is the same failure class the restoreDevice comment
   * below describes for duplicate re-imports, just for the deletion case. */
  deletedDeviceIds: string[]
  isCalibrating: boolean
  calibrationLatency: number
  isMonitoringConnection: boolean

  // Actions
  addDevice: (device: Omit<DeviceInfo, "id" | "lastConnected" | "lastPingSuccess" | "lastPingTime">) => string
  /** Re-inserts a device that already has an id (e.g. pulled back from the
   * server) instead of minting a new one — a no-op if that id is already
   * present. Using addDevice for this would generate a fresh id every time,
   * which is exactly what caused device_settings to grow unbounded: every
   * sync that ran before persisted state finished rehydrating saw an empty
   * local list and re-imported the whole remote set as "new" devices. Also
   * a no-op if the id is tombstoned in deletedDeviceIds. */
  restoreDevice: (device: DeviceInfo) => void
  removeDevice: (id: string) => void
  updateDevice: (id: string, updates: Partial<DeviceInfo>) => void
  setActiveDevice: (id: string | null) => void

  connectDevice: (id: string) => Promise<boolean>
  disconnectDevice: (id: string) => void
  checkDeviceConnection: (id: string) => Promise<boolean>
  testDeviceMovement: (id: string) => Promise<boolean>

  startCalibration: () => void
  completeCalibration: (latencyMs: number) => void
  autoCalibrate: (id: string) => Promise<number>
  /** Records one device's measured offset (ms); playback timing uses an explicit user adjustment. */
  setDeviceSyncDelay: (id: string, ms: number) => void
  /** Measures every connected Handy and sets each one's sync delay. Returns one result per device. */
  autoCalibrateAll: () => Promise<Array<{ id: string; name: string; latencyMs?: number; error?: string }>>

  updateDeviceSettings: (id: string, settings: Partial<DeviceSettings>) => void
  sendToRestPosition: () => void

  getActiveDevice: () => DeviceInfo | null

  startConnectionMonitoring: () => void
  stopConnectionMonitoring: () => void
}

const DEFAULT_DEVICE_SETTINGS: DeviceSettings = {
  minPosition: 0,
  maxPosition: 100,
  restPosition: 0,
  invertControls: false,
  strokeSpeedLimit: 10,
  smoothingEnabled: true,
  smoothingAmount: 30,
  controlMode: "2-axis",
  yOnlySpeedSensitivity: 50,
}

/**
 * Measures a Handy's latency on its own throwaway adapter, so calibrating never rebinds or disturbs
 * the adapter the app is currently playing through (the controller holds one adapter per type).
 */
async function measureHandyLatency(device: { connectionKey?: string; apiVersion?: "v2" | "v3" }): Promise<number> {
  if (!device.connectionKey) throw new Error("No connection key set")
  const handy = new HandyDevice()
  handy.setConnectionKey(device.connectionKey)
  if (device.apiVersion) handy.setApiVersion(device.apiVersion)
  const result = await handy.autoCalibrate(5)
  return result.latencyMs
}

/** Calibration makes the device fire EARLIER by the measured latency, so the delay is negative. */
function syncDelayFromLatency(latencyMs: number): number {
  return -Math.max(0, Math.min(2000, Math.round(latencyMs)))
}

/**
 * Records the active device's calibration in the playback store without shifting playback timing.
 * Imported lazily: the playback store pulls in the engine, which itself imports this store.
 */
function applyActiveSyncDelay(ms: number) {
  void import("./global-playback-store")
    .then((m) => m.useGlobalPlaybackStore.getState().setDeviceSyncDelay(ms))
    .catch((error) => console.warn("[Device] Failed to apply sync delay:", error))
}

export const useDeviceStore = create<DeviceState>()(
  persist(
    (set, get) => ({
      devices: [],
      activeDeviceId: null,
      deletedDeviceIds: [],
      isCalibrating: false,
      calibrationLatency: 0,
      isMonitoringConnection: false,

      addDevice: (deviceData) => {
        const id = `device-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
        const newDevice: DeviceInfo = {
          ...deviceData,
          id,
          lastConnected: Date.now(),
          lastPingSuccess: false,
          lastPingTime: 0,
          settings: { ...DEFAULT_DEVICE_SETTINGS, ...deviceData.settings },
        }
        set((state) => ({ devices: [...state.devices, newDevice] }))
        return id
      },

      restoreDevice: (device) => {
        set((state) =>
          state.devices.some((d) => d.id === device.id) || state.deletedDeviceIds.includes(device.id)
            ? state
            : { devices: [...state.devices, device] }
        )
      },

      removeDevice: (id) => {
        const wasActive = get().activeDeviceId === id
        set((state) => ({
          devices: state.devices.filter((d) => d.id !== id),
          activeDeviceId: state.activeDeviceId === id ? null : state.activeDeviceId,
          deletedDeviceIds: state.deletedDeviceIds.includes(id)
            ? state.deletedDeviceIds
            : [...state.deletedDeviceIds, id],
        }))
        // updateDeviceStatus is only ever called with a truthy device name on
        // connect (see app/vr/page.tsx, script-player.tsx, pattern-library.tsx)
        // -- nothing previously cleared it on disconnect/delete, so deleting
        // the active device left a stale device name displayed as if it were
        // still connected. Only clear it if the device just removed WAS the
        // active one; deleting an inactive row shouldn't touch this at all.
        if (wasActive) {
          try {
            const playbackStore = useScriptPlaybackStore.getState()
            if (playbackStore && typeof playbackStore.updateDeviceStatus === "function") {
              playbackStore.updateDeviceStatus(false, null)
            }
          } catch (error) {
            console.warn("[Device] Failed to clear playback store device status:", error)
          }
        }
      },

      updateDevice: (id, updates) => {
        set((state) => ({
          devices: state.devices.map((d) => (d.id === id ? { ...d, ...updates } : d)),
        }))
      },

      setActiveDevice: (id) => {
        set({ activeDeviceId: id })

        // Update playback store with device connection status
        const device = get().devices.find((d) => d.id === id)
        if (device) {
          // Safe cross-store communication with validation
          try {
            const playbackStore = useScriptPlaybackStore.getState()
            if (playbackStore && typeof playbackStore.setDeviceConnected === 'function') {
              playbackStore.setDeviceConnected(device.isConnected && device.lastPingSuccess)
            }
            if (playbackStore && typeof playbackStore.setDeviceLatency === 'function') {
              playbackStore.setDeviceLatency(device.latencyMs)
            }
          } catch (error) {
            console.warn('[Device] Failed to update playback store device status:', error)
          }
          // Keep the diagnostic calibration associated with the active device.
          applyActiveSyncDelay(device.syncDelayMs ?? 0)
        }
      },

      connectDevice: async (id) => {
        const device = get().devices.find((d) => d.id === id)
        if (!device) return false

        try {
          const connectionKey = device.connectionKey

          // Set active device in controller
          deviceController.setActiveDevice(device.type, connectionKey, device.apiVersion)

          deviceController.setDeviceSettings({
            minPosition: device.settings.minPosition,
            maxPosition: device.settings.maxPosition,
          })

          // For devices that need explicit connection (Buttplug, Intiface)
          if (device.type === "buttplug" || device.type !== "handy") {
            const connected = await deviceController.connect()
            if (!connected) {
              console.error(`Failed to connect to ${device.type} device`)
              return false
            }
          }

          // Test connection
          const isConnected = await deviceController.checkConnection()

          if (isConnected) {
            // Get device info
            const info = await deviceController.getInfo()

            set((state) => ({
              devices: state.devices.map((d) =>
                d.id === id
                  ? {
                      ...d,
                      isConnected: true,
                      lastPingSuccess: true,
                      lastPingTime: Date.now(),
                      lastConnected: Date.now(),
                      firmwareVersion: info?.fwVersion || info?.firmwareVersion || "Unknown",
                    }
                  : d,
              ),
              activeDeviceId: id,
            }))

            // Update playback store with validation
            try {
              const playbackStore = useScriptPlaybackStore.getState()
              if (playbackStore && typeof playbackStore.setDeviceConnected === 'function') {
                playbackStore.setDeviceConnected(true)
              }
            } catch (error) {
              console.warn('[Device] Failed to update playback store connection status:', error)
            }

            // Start connection monitoring
            get().startConnectionMonitoring()

            return true
          }

          return false
        } catch (error) {
          console.error(`Failed to connect to ${device.type}:`, error)
          set((state) => ({
            devices: state.devices.map((d) =>
              d.id === id ? { ...d, isConnected: false, lastPingSuccess: false, lastPingTime: Date.now() } : d,
            ),
          }))
          return false
        }
      },

      disconnectDevice: (id) => {
        const device = get().devices.find((d) => d.id === id)

        if (device) {
          deviceController.disconnect()
        }

        set((state) => ({
          devices: state.devices.map((d) => (d.id === id ? { ...d, isConnected: false, lastPingSuccess: false } : d)),
          isMonitoringConnection: false,
        }))

        // Update playback store with validation
        if (get().activeDeviceId === id) {
          try {
            const playbackStore = useScriptPlaybackStore.getState()
            if (playbackStore && typeof playbackStore.setDeviceConnected === 'function') {
              playbackStore.setDeviceConnected(false)
            }
          } catch (error) {
            console.warn('[Device] Failed to update playback store connection status:', error)
          }
        }
      },

      checkDeviceConnection: async (id) => {
        const device = get().devices.find((d) => d.id === id)
        if (!device) return false

        if (device.connectionKey) {
          deviceController.setActiveDevice(device.type, device.connectionKey, device.apiVersion)
        }

        const isConnected = await deviceController.checkConnection()

        set((state) => ({
          devices: state.devices.map((d) =>
            d.id === id
              ? {
                  ...d,
                  isConnected,
                  lastPingSuccess: isConnected,
                  lastPingTime: Date.now(),
                }
              : d,
          ),
        }))

        // Update playback store if this is the active device with validation
        if (get().activeDeviceId === id) {
          try {
            const playbackStore = useScriptPlaybackStore.getState()
            if (playbackStore && typeof playbackStore.setDeviceConnected === 'function') {
              playbackStore.setDeviceConnected(isConnected)
            }
          } catch (error) {
            console.warn('[Device] Failed to update playback store connection status:', error)
          }
        }

        return isConnected
      },

      testDeviceMovement: async (id) => {
        const device = get().devices.find((d) => d.id === id)
        if (!device || !device.isConnected) return false

        if (device.connectionKey) {
          deviceController.setActiveDevice(device.type, device.connectionKey, device.apiVersion)
        }

        return await deviceController.testMovement()
      },

      startCalibration: () => {
        set({ isCalibrating: true })
      },

      completeCalibration: (latencyMs) => {
        const activeId = get().activeDeviceId
        if (activeId) {
          set((state) => ({
            devices: state.devices.map((d) => (d.id === activeId ? { ...d, latencyMs, syncDelayMs: syncDelayFromLatency(latencyMs) } : d)),
            isCalibrating: false,
            calibrationLatency: latencyMs,
          }))

          // Update playback store with validation
          try {
            const playbackStore = useScriptPlaybackStore.getState()
            if (playbackStore && typeof playbackStore.setDeviceLatency === 'function') {
              playbackStore.setDeviceLatency(latencyMs)
            }
          } catch (error) {
            console.warn('[Device] Failed to update playback store latency:', error)
          }
          applyActiveSyncDelay(syncDelayFromLatency(latencyMs))
        } else {
          set({ isCalibrating: false, calibrationLatency: latencyMs })
        }
      },

      autoCalibrate: async (id) => {
        const device = get().devices.find((d) => d.id === id)
        if (!device || device.type !== "handy" || !device.connectionKey) {
          throw new Error("Device not found or does not support calibration")
        }

        set({ isCalibrating: true })

        try {
          const latencyMs = await measureHandyLatency(device)

          set((state) => ({
            devices: state.devices.map((d) => (d.id === id ? { ...d, latencyMs, syncDelayMs: syncDelayFromLatency(latencyMs) } : d)),
            isCalibrating: false,
            calibrationLatency: latencyMs,
          }))

          // Record the active device's measurement; playback uses an explicit user sync offset.
          if (get().activeDeviceId === id) {
            try {
              const playbackStore = useScriptPlaybackStore.getState()
              if (playbackStore && typeof playbackStore.setDeviceLatency === 'function') {
                playbackStore.setDeviceLatency(latencyMs)
              }
            } catch (error) {
              console.warn('[Device] Failed to update playback store latency:', error)
            }
            applyActiveSyncDelay(syncDelayFromLatency(latencyMs))
          }

          return latencyMs
        } catch (error) {
          set({ isCalibrating: false })
          throw error
        }
      },

      autoCalibrateAll: async () => {
        const targets = get().devices.filter((d) => d.type === "handy" && d.isConnected && d.connectionKey)
        const results: Array<{ id: string; name: string; latencyMs?: number; error?: string }> = []
        if (targets.length === 0) return results

        set({ isCalibrating: true })
        for (const device of targets) {
          try {
            const latencyMs = await measureHandyLatency(device)
            set((state) => ({
              devices: state.devices.map((d) => (d.id === device.id ? { ...d, latencyMs, syncDelayMs: syncDelayFromLatency(latencyMs) } : d)),
            }))
            results.push({ id: device.id, name: device.name, latencyMs })
          } catch (error: any) {
            results.push({ id: device.id, name: device.name, error: error?.message || "Calibration failed" })
          }
        }

        const activeId = get().activeDeviceId
        const activeResult = results.find((r) => r.id === activeId && r.latencyMs !== undefined)
        const shown = activeResult ?? results.find((r) => r.latencyMs !== undefined)
        set({ isCalibrating: false, calibrationLatency: shown?.latencyMs ?? get().calibrationLatency })

        if (activeResult?.latencyMs !== undefined) {
          try {
            const playbackStore = useScriptPlaybackStore.getState()
            if (playbackStore && typeof playbackStore.setDeviceLatency === 'function') {
              playbackStore.setDeviceLatency(activeResult.latencyMs)
            }
          } catch (error) {
            console.warn('[Device] Failed to update playback store latency:', error)
          }
          applyActiveSyncDelay(syncDelayFromLatency(activeResult.latencyMs))
        }
        return results
      },

      setDeviceSyncDelay: (id, ms) => {
        const clamped = Math.max(-2000, Math.min(2000, Math.round(Number.isFinite(ms) ? ms : 0)))
        set((state) => ({
          devices: state.devices.map((d) => (d.id === id ? { ...d, syncDelayMs: clamped } : d)),
        }))
        if (get().activeDeviceId === id) applyActiveSyncDelay(clamped)
      },

      updateDeviceSettings: (id, settings) => {
        set((state) => ({
          devices: state.devices.map((d) => (d.id === id ? { ...d, settings: { ...d.settings, ...settings } } : d)),
        }))

        if (get().activeDeviceId === id) {
          deviceController.setDeviceSettings(settings)
        }
      },

      sendToRestPosition: () => {
        const state = get()
        const active = state.devices.find((d) => d.id === state.activeDeviceId)
        if (!active?.isConnected) return
        deviceController.setPosition(active.settings.restPosition ?? 0, 800)
      },

      getActiveDevice: () => {
        const state = get()
        return state.devices.find((d) => d.id === state.activeDeviceId) || null
      },

      startConnectionMonitoring: () => {
        const state = get()
        
        // Stop any existing monitoring first to prevent multiple intervals
        if (state.isMonitoringConnection) {
          get().stopConnectionMonitoring()
        }

        const activeDevice = state.devices.find((d) => d.id === state.activeDeviceId)
        if (!activeDevice) return

        set({ isMonitoringConnection: true })

        deviceController.setActiveDevice(activeDevice.type, activeDevice.connectionKey, activeDevice.apiVersion)
        deviceController.startConnectionMonitor(async (connected) => {
          const currentState = get()
          const activeId = currentState.activeDeviceId
          
          // Validate monitoring is still active and device hasn't changed
          if (!currentState.isMonitoringConnection || !activeId) {
            console.log('[Device] Monitoring stopped or device changed, ignoring callback')
            return
          }

          const device = currentState.devices.find((d) => d.id === activeId)
          if (!device) return

          // Update connection state
          set((state) => ({
            devices: state.devices.map((d) =>
              d.id === activeId
                ? { ...d, isConnected: connected, lastPingSuccess: connected, lastPingTime: Date.now() }
                : d,
            ),
          }))

          // Safe cross-store communication with validation
          try {
            const playbackStore = useScriptPlaybackStore.getState()
            if (playbackStore && typeof playbackStore.setDeviceConnected === 'function') {
              playbackStore.setDeviceConnected(connected)
            }
          } catch (error) {
            console.warn('[Device] Failed to update playback store connection status:', error)
          }

          if (!connected && device.isConnected) {
            console.log("[Device] Connection lost, attempting to reconnect...")

            // Wait a bit before reconnecting
            await new Promise((r) => setTimeout(r, 2000))

            // Try to reconnect only if monitoring is still active
            if (get().isMonitoringConnection) {
              const reconnected = await get().connectDevice(activeId)

              if (reconnected) {
                console.log("[Device] Successfully reconnected")
              } else {
                console.log("[Device] Reconnection failed")
              }
            }
          }
        }, 20000)
      },

      stopConnectionMonitoring: () => {
        const state = get()
        if (state.isMonitoringConnection) {
          deviceController.stopConnectionMonitor()
        }
        set({ isMonitoringConnection: false })
      },
    }),
    {
      name: "device-storage",
      onRehydrateStorage: () => (state) => {
        if (!state) return

        // One-time cleanup: older builds of the quick-connect modal called
        // addDevice() on every "Connect" click with no de-dupe check, so
        // repeat clicks/retries silently piled up duplicate rows (same
        // type + connectionType) that persisted forever. Collapse those
        // down to the single most-recently-connected entry per
        // type+connectionType pair before anything else runs.
        const seen = new Map<string, DeviceInfo>()
        for (const d of state.devices) {
          const key = `${d.type}:${d.connectionType}`
          const prev = seen.get(key)
          if (!prev || (d.lastConnected ?? 0) >= (prev.lastConnected ?? 0)) {
            seen.set(key, prev ? { ...d, connectionKey: d.connectionKey ?? prev.connectionKey } : d)
          }
        }
        const deduped = Array.from(seen.values())
        if (deduped.length !== state.devices.length) {
          state.devices = deduped
          if (state.activeDeviceId && !deduped.some((d) => d.id === state.activeDeviceId)) {
            state.activeDeviceId = null
          }
        }

        // Mark all persisted devices as disconnected on load — user must
        // connect manually. The connectionKey is kept so the UI can pre-fill it.
        state.devices = state.devices.map((d) => ({
          ...d,
          isConnected: false,
          lastPingSuccess: false,
        }))

        // Restore the active device's saved diagnostic measurement.
        const active = state.devices.find((d) => d.id === state.activeDeviceId)
        applyActiveSyncDelay(active?.syncDelayMs ?? 0)

        // Reconnect the active Wi-Fi Handy by itself, so the user doesn't have to open Devices and press
        // Connect after every reload. The saved key is used, so nothing is typed. A failure is quiet: the
        // device simply stays disconnected (and keeps its key) for a manual Connect.
        if (typeof window !== "undefined" && active?.type === "handy" && active.connectionType === "wifi" && active.connectionKey) {
          const activeId = active.id
          setTimeout(() => {
            void useDeviceStore
              .getState()
              .connectDevice(activeId)
              .catch((error) => console.warn("[Device] Auto-reconnect failed:", error))
          }, 1500)
        }
      },
    },
  ),
)
